import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';

const REQUIRED=Object.freeze([
  'dataContractGateway',
  'providerIngestion',
  'secCompanyFacts',
  'alphaVantageDaily',
  'companyResearchPackage',
  'marketCanonicalization',
  'researchCompositionRoot',
  'endToEndRuntime',
  'uiRuntime',
  'persistenceAdapter',
  'repository',
  'revisionConcurrency',
  'eventJournal',
  'revisionAwareRepository',
  'revisionAwareSession',
  'batchOrchestrator'
]);

function cp(x){return x==null?x:JSON.parse(JSON.stringify(x));}
function freeze(v){
  if(v&&typeof v==='object'&&!Object.isFrozen(v)){
    Object.freeze(v);
    for(const x of Object.values(v))freeze(x);
  }
  return v;
}
function normSymbol(v){return String(v||'').trim().toUpperCase();}

function detect(root=g){
  return Object.freeze({
    dataContractGateway:root.ZooInvestRuntimeDataContractGateway?{ready:true}:null,
    providerIngestion:root.ZooInvestRuntimeProviderIngestionBridge?{ready:true}:null,
    secCompanyFacts:root.ZooInvestSECCompanyFactsProviderAdapter?{ready:true}:null,
    alphaVantageDaily:root.ZooInvestAlphaVantageDailyProviderAdapter?{ready:true}:null,
    companyResearchPackage:root.ZooInvestCompanyResearchDataPackage?{ready:true}:null,
    marketCanonicalization:root.ZooInvestMarketDailyCanonicalSnapshot?{ready:true}:null,
    researchCompositionRoot:root.ZooInvestResearchPipelineCompositionRoot?{ready:true}:null,
    endToEndRuntime:root.ZooInvestEndToEndResearchRuntimeV405?{ready:true}:null,
    uiRuntime:root.ZooInvestRuntimeUIV406?{ready:true}:null,
    persistenceAdapter:root.ZooInvestRevisionAwarePersistenceAdapter?{ready:true}:null,
    repository:root.ZooInvestResearchStateRepositoryEngine?{ready:true}:null,
    revisionConcurrency:root.ZooInvestResearchStateRevisionConcurrencyEngine?{ready:true}:null,
    eventJournal:root.ZooInvestResearchStateEventJournal?{ready:true}:null,
    revisionAwareRepository:root.ZooInvestRevisionAwareRepository?{ready:true}:null,
    revisionAwareSession:root.ZooInvestRevisionAwareRuntimeSession?{ready:true}:null,
    batchOrchestrator:root.ZooInvestResearchBatchOrchestrator?{ready:true}:null
  });
}

function inspect(capabilities={}){
  const rows=REQUIRED.map(name=>{
    const c=capabilities[name];
    if(!c)return {name,state:'MISSING',reason:'NOT_REGISTERED'};
    if(c.error)return {name,state:'BROKEN',reason:String(c.error)};
    if(c.ready===false)return {name,state:'REVIEW',reason:c.reason||'NOT_READY'};
    return {name,state:'READY',reason:null};
  });
  const missing=rows.filter(x=>x.state==='MISSING');
  const broken=rows.filter(x=>x.state==='BROKEN');
  const review=rows.filter(x=>x.state==='REVIEW');
  return freeze({
    version:VERSION,
    status:(missing.length||broken.length)?'BLOCK':review.length?'REVIEW':'READY',
    requiredCount:rows.length,
    readyCount:rows.filter(x=>x.state==='READY').length,
    missingCount:missing.length,
    brokenCount:broken.length,
    reviewCount:review.length,
    capabilities:rows
  });
}

function report(root=g){return inspect(detect(root));}

function create(opts={}){
  const root=opts.root||g;
  const session=opts.session||(root.ZooInvestRevisionAwareRuntimeSession&&root.ZooInvestRevisionAwareRuntimeSession.create(opts.sessionOptions||{}));
  if(!session||typeof session.run!=='function')throw Error('REVISION_AWARE_SESSION_REQUIRED');

  const batchFactory=opts.batchFactory||root.ZooInvestResearchBatchOrchestrator;
  if(!batchFactory||typeof batchFactory.create!=='function')throw Error('BATCH_ORCHESTRATOR_REQUIRED');

  const now=opts.now||(()=>new Date().toISOString());

  function readiness(){
    return report(root);
  }

  async function start(config={}){
    const r=readiness();
    if(r.status==='BLOCK'){
      return freeze({version:VERSION,status:'BLOCK',stage:'CAPABILITY_CHECK',readiness:r,restore:null});
    }
    const symbol=normSymbol(config.symbol);
    if(!symbol){
      return freeze({version:VERSION,status:r.status,stage:'SESSION_READY',readiness:r,restore:null});
    }
    const restore=await session.restore(symbol,config.scope||'RESEARCH');
    if(restore.status==='REVIEW'){
      return freeze({version:VERSION,status:'REVIEW',stage:'RESTORE',readiness:r,restore});
    }
    return freeze({
      version:VERSION,
      status:r.status==='REVIEW'?'REVIEW':'READY',
      stage:'SESSION_READY',
      readiness:r,
      restore
    });
  }

  async function runOne(config={},domRoot){
    const started=await start(config);
    if(started.status==='BLOCK'||started.stage==='RESTORE')return started;

    const symbol=normSymbol(config.symbol);
    if(!symbol){
      return freeze({...cp(started),status:'REVIEW',stage:'RUNTIME',error:'SYMBOL_REQUIRED'});
    }

    const result=await session.run({...config,symbol},domRoot);
    let status=result.status||'REVIEW';
    if(started.status==='REVIEW')status='REVIEW';

    return freeze({
      version:VERSION,
      status,
      stage:'COMPLETE',
      readiness:started.readiness,
      restore:started.restore,
      result,
      guards:{
        capabilityCheckPrecedesRuntime:true,
        revisionAwarePersistenceRequired:true,
        staleWriteCannotBecomeReady:true,
        noTradeSignal:true,
        noCapitalPermission:true,
        noUniversalScore:true
      }
    });
  }

  async function runBatch(config={}){
    const started=await start({});
    if(started.status==='BLOCK')return freeze({...cp(started),stage:'CAPABILITY_CHECK'});

    const pipeline={
      run:async item=>{
        const r=await session.run(item,null);
        return {status:r.status||'REVIEW',session:r};
      }
    };
    const batch=batchFactory.create({pipeline,now});
    const result=await batch.runBatch(config.items||[],config.budgetMinutes||60);

    let status=started.status==='REVIEW'?'REVIEW':'READY';
    if(result.summary.reviewCount>0)status='REVIEW';

    return freeze({
      version:VERSION,
      status,
      stage:'COMPLETE',
      readiness:started.readiness,
      batch:result,
      guards:{
        capabilityCheckPrecedesBatch:true,
        batchUsesRevisionAwareSession:true,
        finiteBudgetPreserved:true,
        noTradeSignal:true,
        noCapitalPermission:true,
        noUniversalScore:true
      }
    });
  }

  return Object.freeze({VERSION,readiness,start,runOne,runBatch,session});
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const all={};
  REQUIRED.forEach(n=>all[n]={ready:true});
  const ir=inspect(all);
  ok('all extended capabilities ready',ir.status==='READY');
  ok('extended ready count complete',ir.readyCount===REQUIRED.length);

  const missing=cp(all);delete missing.alphaVantageDaily;
  const mr=inspect(missing);
  ok('missing provider blocks',mr.status==='BLOCK');
  ok('missing provider named',mr.capabilities.find(x=>x.name==='alphaVantageDaily').state==='MISSING');

  const review=cp(all);review.revisionAwareRepository={ready:false,reason:'CONFIG_PENDING'};
  ok('not-ready repository reviews',inspect(review).status==='REVIEW');

  const fakeRoot={
    ZooInvestRuntimeDataContractGateway:{},
    ZooInvestRuntimeProviderIngestionBridge:{},
    ZooInvestSECCompanyFactsProviderAdapter:{},
    ZooInvestAlphaVantageDailyProviderAdapter:{},
    ZooInvestCompanyResearchDataPackage:{},
    ZooInvestMarketDailyCanonicalSnapshot:{},
    ZooInvestResearchPipelineCompositionRoot:{},
    ZooInvestEndToEndResearchRuntimeV405:{},
    ZooInvestRuntimeUIV406:{},
    ZooInvestRevisionAwarePersistenceAdapter:{},
    ZooInvestResearchStateRepositoryEngine:{},
    ZooInvestResearchStateRevisionConcurrencyEngine:{},
    ZooInvestResearchStateEventJournal:{},
    ZooInvestRevisionAwareRepository:{},
    ZooInvestRevisionAwareRuntimeSession:{},
    ZooInvestResearchBatchOrchestrator:{}
  };
  ok('global detection complete',report(fakeRoot).status==='READY');

  const records=new Map();
  const fakeSession={
    async restore(symbol,scope){
      const k=scope+':'+symbol;
      return records.has(k)?{status:'RESTORED',symbol,scope,record:records.get(k)}:{status:'EMPTY',symbol,scope,record:null};
    },
    async run(req){
      const symbol=normSymbol(req.symbol),scope=req.scope||'RESEARCH',k=scope+':'+symbol;
      const old=records.get(k),revision=(old?.revision||0)+1;
      const rec={symbol,scope,revision};records.set(k,rec);
      return {status:req.forceReview?'REVIEW':'READY',symbol,scope,persistence:{state:'SAVED',revision},guards:{revisionConcurrencyEnforced:true}};
    }
  };
  const fakeBatchFactory={
    create:({pipeline,now})=>({
      async runBatch(items,budgetMinutes){
        const selected=items.slice(0,Math.max(0,Math.floor(budgetMinutes/5)));
        const results=[];
        for(const x of selected){
          const r=await pipeline.run(x);
          results.push({symbol:x.symbol,status:r.status,result:r});
        }
        return {
          summary:{batchId:'batch-'+now(),selectedCount:selected.length,deferredCount:items.length-selected.length,readyCount:results.filter(x=>x.status==='READY').length,reviewCount:results.filter(x=>x.status!=='READY').length,budgetMinutes,usedMinutes:selected.length*5},
          results
        };
      }
    })
  };

  const api=create({root:fakeRoot,session:fakeSession,batchFactory:fakeBatchFactory,now:()=> 'T'});
  const s=await api.start({symbol:'aaa',scope:'OWNED'});
  ok('coordinator starts with restore',s.stage==='SESSION_READY'&&s.restore.status==='EMPTY');

  const one=await api.runOne({symbol:'aaa',scope:'OWNED'},null);
  ok('single runtime completes',one.stage==='COMPLETE');
  ok('single runtime ready',one.status==='READY');
  ok('single runtime persisted revision',one.result.persistence.revision===1);
  ok('capability check guard',one.guards.capabilityCheckPrecedesRuntime===true);

  const one2=await api.runOne({symbol:'AAA',scope:'OWNED'},null);
  ok('second run revision increments',one2.result.persistence.revision===2);

  const br=await api.runBatch({items:[{symbol:'BBB',scope:'WATCHLIST'},{symbol:'CCC',scope:'RESEARCH'}],budgetMinutes:10});
  ok('batch completes',br.stage==='COMPLETE');
  ok('batch selected both',br.batch.summary.selectedCount===2);
  ok('batch uses revision-aware session',br.guards.batchUsesRevisionAwareSession===true);
  ok('finite budget guard',br.guards.finiteBudgetPreserved===true);

  const br2=await api.runBatch({items:[{symbol:'DDD',forceReview:true}],budgetMinutes:5});
  ok('batch review propagates',br2.status==='REVIEW');

  let e1=false;try{create({root:fakeRoot,session:null,batchFactory:fakeBatchFactory})}catch(e){e1=e.message==='REVISION_AWARE_SESSION_REQUIRED'}
  /* explicit null falls back to root factory, so test with root missing session */
  const badRoot={...fakeRoot};delete badRoot.ZooInvestRevisionAwareRuntimeSession;
  let e2=false;try{create({root:badRoot,batchFactory:fakeBatchFactory})}catch(e){e2=e.message==='REVISION_AWARE_SESSION_REQUIRED'}
  ok('revision aware session required',e2);

  ok('api no trade signal',!('tradeSignal'in api));
  ok('api no capital permission',!('capitalPermission'in api));
  ok('api no universal score',!('score'in api));

  return freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:t});
}

g.ZooInvestRuntimeCapabilityReadinessV409=Object.freeze({VERSION,REQUIRED,detect,inspect,report});
g.ZooInvestCoordinatedResearchSessionV409=Object.freeze({VERSION,create,selfTest});
})(engineContext);